import { Box, Flex, Grid, Icon, Stack, Text } from '@chakra-ui/react';
import { RecipeDetailResponse } from '@diamond/shared/types';
import {
  IconButton,
  PlateSerializer,
  RecipeDescriptionCard,
  RecipeImagesSlider,
  RecipeProductTabs,
  RecipeTag,
} from '@diamond/shared/ui';
import { trackEvent } from '@diamond/shared/utils';
import { TRACKING_EVENTS, TRACKING_PAGES } from '@diamond/sol-buyer/constant';
import { useGetRecipeDetail, useShipToStore } from '@diamond/sol-buyer-context';
import { ChevronLeftOutlined, HelpOutline } from '@mui/icons-material';
import Link from 'next/link';
import { useRouter } from 'next/router';

import { Image } from '../../common';
import { FavoriteRecipeIcon } from '../icons';
import { RecipeCardMobile } from '../RecipeCardMobile';
import { RecipeCartBottomSheet } from './cart/RecipeCartBottomSheet';
import { useRecipeDetail } from './useRecipeDetail';
import { YoutubePlayer } from './YoutubePlayer';

type RecipeDetailType = {
  data: RecipeDetailResponse;
  onFavoriteClick: (id: string) => void;
  isAuthenticated: boolean;
};

export const RecipeDetailMobile = (props: RecipeDetailType) => {
  const router = useRouter();
  const slug = router.query['recipesId'] as string;
  const warehouse = useShipToStore((s) => s.selectedShipToId);

  const { dataUpdatedAt } = useGetRecipeDetail(slug, warehouse);
  const {
    countCartItems,
    getHppPrice,
    isRecommended,
    setRecommended,
    hasAlternativeProducts,
    hasRecommendedProducts,
  } = useRecipeDetail({
    data: props.data,
    isAuthenticated: props.isAuthenticated,
  });

  return (
    <Box>
      <Box
        flex="none"
        isolation="isolate"
        position="relative"
        overflow="hidden"
      >
        {props.data.medias ? (
          <IconButton
            aria-label="favorite-icon"
            fontSize="24px"
            variant="unstyled"
            name="favorite-icon"
            icon={<ChevronLeftOutlined fontSize="inherit" />}
            p={1}
            position="absolute"
            textColor="white"
            bg="rgb(0,0,0,0.3)"
            borderColor="white"
            left="5%"
            top="5%"
            zIndex={9999}
            onClick={() => {
              router.back();
            }}
          />
        ) : null}
        {props.data.medias ? (
          <Link href="/recipes/favorite">
            <IconButton
              aria-label="favorite-icon"
              fontSize="24px"
              variant="unstyled"
              name="favorite-icon"
              icon={<FavoriteRecipeIcon fontSize="inherit" />}
              p={1}
              position="absolute"
              textColor="white"
              bg="rgb(0,0,0,0.3)"
              borderColor="white"
              right="5%"
              top="5%"
              zIndex={9999}
            />
          </Link>
        ) : null}

        {props.data.medias ? <RecipeImagesSlider recipe={props.data} /> : null}
        {props.data.thumbnail === '' ? (
          <Box
            w={{ base: '380px', md: '300px', xl: '400px' }}
            h={{ base: '380px', md: '300px', xl: '400px' }}
            flex="none"
            isolation="isolate"
            position="relative"
            borderRadius="lg"
            overflow="hidden"
            mb="4"
          >
            <Image
              alt="Recipe Illustration"
              src="/images/product_placeholder.png"
              fill
              style={{ borderRadius: 'inherit', objectFit: 'cover' }}
            />
          </Box>
        ) : null}
        {props.data.video ? (
          <YoutubePlayer
            videoUrl={props.data.video}
            p={5}
            position="absolute"
            textColor="white"
            bg="rgb(0,0,0,0.3)"
            borderColor="white"
            left="50%"
            bottom="15%"
            transform="translateX(-50%)"
            variant="outline"
            handleTrackEvent={() =>
              trackEvent('click_recipe_tutorial', {
                video_url: props.data.video ?? '',
                id: props.data.id,
                name: props.data.title,
              })
            }
          />
        ) : null}
      </Box>
      <Stack position="relative" spacing={3} px="4" mt={-8}>
        <Stack>
          <RecipeDescriptionCard
            recipe={props.data}
            handleFavorite={props.onFavoriteClick}
            isFavorite={props.data.is_favorite}
          />
          {props.data.ingredient_groups.length > 0 ? (
            <Box borderRadius="lg" overflow="hidden" p={2} bg="white">
              <Flex p={2} justifyContent="space-between">
                <Text fontSize="xl" fontWeight="bold">
                  Bahan & Produk
                </Text>
                {props.data.portion_size ? (
                  <RecipeTag
                    tag={{
                      id: '',
                      bg_color: '#FFF2E2',
                      name: `${props.data.portion_size} Porsi`,
                      text_color: '#A26E2C',
                      big_icon_url: '',
                      small_icon_url: '/images/recipe-icons/cutlery.svg',
                    }}
                  />
                ) : null}
              </Flex>
              <RecipeProductTabs
                data={props.data}
                isAuthenticated={props.isAuthenticated}
                hasAlternativeProducts={hasAlternativeProducts}
                hasRecommendedProducts={hasRecommendedProducts}
                onClickRecommended={() => setRecommended(true)}
                onClickAlternative={() => setRecommended(false)}
              />
            </Box>
          ) : null}
          {Array.isArray(props.data.body) && props.data.body.length > 0 ? (
            <Stack>
              <Box borderRadius="lg" overflow="hidden" p={4} bg="white" pb="0">
                <Flex mb="6" justifyContent="space-between">
                  <Text fontSize="xl" fontWeight="bold">
                    Instruksi Pembuatan
                  </Text>
                  {props.data.portion_size ? (
                    <RecipeTag
                      tag={{
                        id: '',
                        bg_color: '#D6F4D7',
                        name: `${props.data.making_duration} Menit`,
                        text_color: '#0D4949',
                        big_icon_url: '',
                        small_icon_url: '/images/recipe-icons/acute.svg',
                      }}
                    />
                  ) : null}
                </Flex>
                <PlateSerializer body={props.data.body} />
              </Box>
            </Stack>
          ) : null}
          {props.data.similar_recipes.length > 0 ? (
            <Stack mt="8 !important">
              <Text fontWeight="bold" fontSize="xl">
                Resep Dengan Bahan Serupa
              </Text>
              <Grid
                templateColumns="repeat(3, minmax(0, 1fr))"
                columnGap="3"
                rowGap="6"
              >
                {props.data.similar_recipes.map((recipe) => {
                  return (
                    <RecipeCardMobile
                      recipe={recipe}
                      handleTrackEvent={(recipe) => {
                        trackEvent(TRACKING_EVENTS.SELECT_RECIPE, {
                          id: recipe.id,
                          name: recipe.title,
                          brand: recipe.brands.toString(),
                          section_name: TRACKING_PAGES.RECIPE_DETAIL,
                        });
                      }}
                      section={TRACKING_PAGES.RECIPE_DETAIL}
                    />
                  );
                })}
              </Grid>
            </Stack>
          ) : null}
        </Stack>
      </Stack>
      <Box
        as={Flex}
        borderTopRadius="3xl"
        bg="white"
        px={6}
        py={3}
        w="full"
        position="fixed"
        minH="100px"
        bottom={0}
        insetInline={0}
        zIndex={999}
        alignItems="center"
        boxShadow="0 -20px 10px -5px rgba(0, 0, 0, 0.1),0 10px 10px -5px rgba(0, 0, 0, 0.04)"
      >
        <Stack spacing="0" flex="0 0 60%">
          <Text fontSize="xs" color="#006BD1">
            Estimasi HPP per Porsi{' '}
            <Icon mt="-1" fontSize="18px" as={HelpOutline} />
          </Text>
          <Text fontSize="xl" fontWeight="bold">
            {getHppPrice()}
          </Text>
        </Stack>
        <RecipeCartBottomSheet
          key={`${slug}${dataUpdatedAt}${isRecommended}`}
          data={props.data}
          countItems={countCartItems()}
          isDisabled={
            isRecommended ? !hasRecommendedProducts : !hasAlternativeProducts
          }
          isRecommended={isRecommended}
        />
      </Box>
    </Box>
  );
};
